import {
  InfoPopover,
  Modal,
  Select,
  TrackedButton,
  Typography,
  useDurationPickerField,
} from '@automata/ui';
import { MenuItem, Stack } from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { useCallback, useEffect, useState } from 'react';

import { Consistency } from './components/Consistency/Consistency';
import { TimeConstraintParameters } from './components/TimeConstraintParameters/TimeConstraintParameters';
import { TimingTargetParams } from './components/TimingTargetParams/TimingTargetParams';

type TimeConstraintModalMode = 'add' | 'edit';

export type TimeConstraintHardData = {
  kind: 'time_constraint';
  minDuration: number | null | undefined;
  maxDuration: number | null | undefined;
  enableDetailedCoverageCalculation: boolean;
  autoIgnoreIfBroken: boolean;
  hardConstraintId?: string;
};

export type TimingTargetSoftData = {
  kind: 'timing_target';
  target: number;
  weight: number;
  timingTargetId?: string;
};

export type ConsistencyTargetSoftData = {
  kind: 'consistency_target';
  consistencyTargetId?: string;
};

export type TimeConstraintModalData =
  | TimeConstraintHardData
  | TimingTargetSoftData
  | ConsistencyTargetSoftData;

interface TimeConstraintModalProps {
  open: boolean;
  mode: TimeConstraintModalMode;
  initialData?: TimeConstraintModalData;
  initialRuleType?: 'time_constraint' | 'timing_target' | 'consistency_target';
  hasConsistencyRule?: boolean;
  onClose: () => void;
  onSave: (data: TimeConstraintModalData) => void;
  onDelete?: () => void;
}

const RULE_TYPE_TOOLTIP = (
  <div>
    <p>
      <b>Time Constraint (Hard)</b>
      <br />
      Enforces a strict timing window between two task edges. You can define a
      minimum time, a maximum time, or both.
    </p>

    <p>
      <b>Timing Target (Soft)</b>
      <br />
      Specifies the ideal time interval between two task edges.
    </p>

    <p>
      <b>Consistency Target (Soft)</b>
      <br />
      Defines two tasks as targets for consistent separation across batches. By
      default, consistency applies per piece of labware; this setting tells the
      solver to prioritize consistency between these tasks.
    </p>
  </div>
);

const DEFAULT_FORM_DATA: TimeConstraintHardData = {
  kind: 'time_constraint',
  minDuration: null,
  maxDuration: null,
  enableDetailedCoverageCalculation: true,
  autoIgnoreIfBroken: false,
};

export const RULE_TYPE_OPTIONS = [
  {
    value: 'time_constraint',
    label: 'Time constraint (hard)',
  },
  {
    value: 'timing_target',
    label: 'Timing target (soft)',
  },
  {
    value: 'consistency_target',
    label: 'Consistency target (soft)',
  },
];

export const TimeConstraintModal = ({
  open,
  mode,
  initialData,
  initialRuleType,
  hasConsistencyRule,
  onClose,
  onSave,
  onDelete,
}: TimeConstraintModalProps) => {
  const [formData, setFormData] = useState<TimeConstraintModalData>(
    initialData ?? DEFAULT_FORM_DATA
  );
  const [ruleType, setRuleType] = useState(
    initialRuleType ?? RULE_TYPE_OPTIONS[0]?.value ?? ''
  );
  const [minTotalSeconds, setMinTotalSeconds] = useState<
    number | null | undefined
  >(initialData?.kind === 'time_constraint' ? initialData.minDuration : null);
  const [maxTotalSeconds, setMaxTotalSeconds] = useState<
    number | null | undefined
  >(
    initialData &&
      'kind' in initialData &&
      initialData.kind === 'time_constraint'
      ? (initialData as TimeConstraintHardData).maxDuration
      : null
  );

  const [idealTargetSeconds, setIdealTargetSeconds] = useState<
    number | null | undefined
  >(
    initialData && 'kind' in initialData && initialData.kind === 'timing_target'
      ? (initialData as TimingTargetSoftData).target
      : null
  );
  const [priorityWeight, setPriorityWeight] = useState<number>(
    initialData && 'kind' in initialData && initialData.kind === 'timing_target'
      ? (initialData as TimingTargetSoftData).weight
      : 1
  );

  const minValue = minTotalSeconds;
  const maxValue = maxTotalSeconds;

  const {
    breakdown: minBreakdown,
    handleUpdateTime: onMinUpdate,
    handleBlur: onMinBlur,
  } = useDurationPickerField(setMinTotalSeconds, minValue);

  const {
    breakdown: maxBreakdown,
    handleUpdateTime: onMaxUpdate,
    handleBlur: onMaxBlur,
  } = useDurationPickerField(setMaxTotalSeconds, maxValue);

  const {
    breakdown: idealBreakdown,
    handleUpdateTime: onIdealUpdate,
    handleBlur: onIdealBlur,
  } = useDurationPickerField(setIdealTargetSeconds, idealTargetSeconds);

  useEffect(() => {
    if (!open) return;
    const seed = initialData ?? DEFAULT_FORM_DATA;
    setFormData(seed);
    setRuleType(
      initialRuleType ?? seed.kind ?? RULE_TYPE_OPTIONS[0]?.value ?? ''
    );

    if (seed.kind === 'timing_target') {
      setIdealTargetSeconds(seed.target);
      setPriorityWeight(seed.weight);
      setMinTotalSeconds(null);
      setMaxTotalSeconds(null);
    } else if (seed.kind === 'time_constraint') {
      setMinTotalSeconds(seed.minDuration);
      setMaxTotalSeconds(seed.maxDuration);
      setIdealTargetSeconds(null);
      setPriorityWeight(1);
    } else {
      setMinTotalSeconds(null);
      setMaxTotalSeconds(null);
      setIdealTargetSeconds(null);
      setPriorityWeight(1);
    }
  }, [open, initialData, initialRuleType]);

  const hasAnyDuration = minValue !== null || maxValue !== null;
  const hasIdealTime =
    idealTargetSeconds !== null && idealTargetSeconds !== undefined;

  const isAddMode = mode === 'add';
  const title = isAddMode ? 'Add rule' : 'Edit rule';

  const onRuleTypeChange = useCallback((event: SelectChangeEvent<unknown>) => {
    setRuleType(event.target.value as string);
  }, []);

  const onDetailedCoverageToggle = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const checked = event.target.checked;
      setFormData((prev) => ({
        ...prev,
        enableDetailedCoverageCalculation: checked,
      }));
    },
    []
  );

  const onAutoIgnoreToggle = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const checked = event.target.checked;
      setFormData((prev) => ({
        ...prev,
        autoIgnoreIfBroken: checked,
      }));
    },
    []
  );

  const handleSave = useCallback(() => {
    if (ruleType === 'timing_target') {
      if (!hasIdealTime) return;
      onSave({
        kind: 'timing_target',
        target: idealTargetSeconds!,
        weight: priorityWeight,
        timingTargetId: (initialData as TimingTargetSoftData | undefined)
          ?.timingTargetId,
      });
      onClose();
      return;
    }

    if (ruleType === 'consistency_target') {
      onSave({
        kind: 'consistency_target',
        consistencyTargetId: (initialData as any)?.consistencyTargetId,
      });
      onClose();
      return;
    }

    if (!hasAnyDuration) return;
    onSave({
      kind: 'time_constraint',
      minDuration: minValue,
      maxDuration: maxValue,
      enableDetailedCoverageCalculation: (formData as TimeConstraintHardData)
        .enableDetailedCoverageCalculation,
      autoIgnoreIfBroken: (formData as TimeConstraintHardData)
        .autoIgnoreIfBroken,
      hardConstraintId: (initialData as TimeConstraintHardData | undefined)
        ?.hardConstraintId,
    });
    onClose();
  }, [
    ruleType,
    hasIdealTime,
    idealTargetSeconds,
    priorityWeight,
    hasAnyDuration,
    onSave,
    minValue,
    maxValue,
    formData,
    onClose,
    initialData,
  ]);

  return (
    <Modal open={open} onClose={onClose} title={title}>
      <Stack spacing={3} mt={1}>
        <Select
          label="RULE TYPE"
          value={ruleType}
          onChange={onRuleTypeChange}
          infoPopover={
            <InfoPopover title="Rule type" copy={RULE_TYPE_TOOLTIP} />
          }
        >
          {RULE_TYPE_OPTIONS.map(({ value, label }) => {
            const isConsistencyDisabled =
              value === 'consistency_target' &&
              hasConsistencyRule &&
              initialData?.kind !== 'consistency_target';

            return (
              <MenuItem
                key={value}
                value={value}
                disabled={isConsistencyDisabled}
              >
                {label}
              </MenuItem>
            );
          })}
        </Select>

        <Stack spacing={1}>
          <Typography variant="body1" color="text.primary">
            Rule configuration
          </Typography>
        </Stack>

        {ruleType === 'time_constraint' && (
          <TimeConstraintParameters
            minBreakdown={minBreakdown}
            maxBreakdown={maxBreakdown}
            onMinUpdate={onMinUpdate}
            onMaxUpdate={onMaxUpdate}
            onMinBlur={onMinBlur}
            onMaxBlur={onMaxBlur}
            formData={formData}
            handleChange={onDetailedCoverageToggle}
            onAutoIgnoreToggle={onAutoIgnoreToggle}
          />
        )}

        {ruleType === 'timing_target' && (
          <TimingTargetParams
            idealBreakdown={idealBreakdown}
            priorityWeight={priorityWeight}
            setPriorityWeight={setPriorityWeight}
            onIdealBlur={onIdealBlur}
            onIdealUpdate={onIdealUpdate}
          />
        )}

        {ruleType === 'consistency_target' && <Consistency />}

        <Stack direction="row" justifyContent="space-between">
          {!isAddMode && onDelete ? (
            <TrackedButton
              trackLabel="workflowBuilder.timeConstraintModal.Delete"
              color="error"
              variant="contained"
              onClick={onDelete}
            >
              Delete rule
            </TrackedButton>
          ) : (
            <span />
          )}

          <Stack direction="row" spacing={2}>
            <TrackedButton
              trackLabel="workflowBuilder.timeConstraintModal.Cancel"
              color="primary"
              variant="text"
              onClick={onClose}
            >
              Cancel
            </TrackedButton>
            <TrackedButton
              trackLabel={`workflowBuilder.timeConstraintModal.${
                isAddMode ? 'Add' : 'Save'
              }`}
              color="primary"
              variant="contained"
              onClick={handleSave}
              disabled={
                ruleType === 'time_constraint'
                  ? !hasAnyDuration
                  : ruleType === 'timing_target'
                  ? !hasIdealTime
                  : false
              }
            >
              {isAddMode ? 'Add rule' : 'Save changes'}
            </TrackedButton>
          </Stack>
        </Stack>
      </Stack>
    </Modal>
  );
};
